import { Link, Navigate, useNavigate } from '@tanstack/react-router'
import type { CaseStage } from '@tex/finance-shared'
import { Plus } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { Alert, Button, Field, Input, Mono, Select, StageBadge } from '../components/ui'
import { errorMessage } from '../lib/api'
import { useCases } from '../lib/cases'
import { useDealer } from '../lib/dealers'
import { formatAmount, formatDate, STAGE_LABELS } from '../lib/format'
import { isTexStaff, useMe } from '../lib/session'

export interface CasesSearch {
  stage?: CaseStage
  q?: string
  /** Dealer membership to act for; only meaningful for users who belong to several dealers. */
  dealer?: string
}

const STAGES = Object.keys(STAGE_LABELS) as CaseStage[]

function DealerOption({ dealerId }: { dealerId: string }) {
  const dealer = useDealer(dealerId)
  return <option value={dealerId}>{dealer.data ? (dealer.data.trading_name ?? dealer.data.legal_name) : dealerId.slice(0, 8)}</option>
}

/** Keyed on `search.q` by the caller so the input resets when the URL changes (back/forward, links). */
function SearchForm({ search, setSearch }: { search: CasesSearch; setSearch: (patch: Partial<CasesSearch>) => void }) {
  const [q, setQ] = useState(search.q ?? '')
  const submit = (e: FormEvent) => {
    e.preventDefault()
    setSearch({ q: q.trim() || undefined })
  }
  return (
    <form onSubmit={submit} className="flex flex-wrap items-end gap-2">
      <Field label="Search">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buyer, contact or reference" className="w-64" />
      </Field>
      <Field label="Stage">
        <Select value={search.stage ?? ''} onChange={(e) => setSearch({ stage: (e.target.value || undefined) as CaseStage | undefined })}>
          <option value="">All</option>
          {STAGES.map((s) => (
            <option key={s} value={s}>
              {STAGE_LABELS[s]}
            </option>
          ))}
        </Select>
      </Field>
      <Button type="submit" variant="secondary">
        Search
      </Button>
    </form>
  )
}

export function CasesListPage({ search }: { search: CasesSearch }) {
  const me = useMe()
  const navigate = useNavigate()

  const tex = isTexStaff(me.data)
  const memberships = me.data?.dealers ?? []
  const dealerId = tex ? undefined : (search.dealer ?? (memberships.length === 1 ? memberships[0]?.dealer_id : undefined))
  const needsDealer = !tex && memberships.length > 1 && !dealerId
  const activeDealer = memberships.find((m) => m.dealer_id === dealerId)
  const canCreate = !tex && activeDealer?.status === 'active'

  const cases = useCases({ stage: search.stage, q: search.q, dealerId }, Boolean(me.data) && !needsDealer)
  const setSearch = (patch: Partial<CasesSearch>) =>
    void navigate({ to: '/cases', search: (prev: CasesSearch) => ({ ...prev, ...patch }), replace: true })

  if (me.data === null) return <Navigate to="/sign-in" />
  if (me.data && !tex && memberships.length === 0) {
    return <Alert tone="error">Financing requests are created by dealership team members. Ask your dealership to invite you.</Alert>
  }

  const rows = cases.data?.pages.flatMap((p) => p.cases) ?? []

  return (
    <section className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-neutral-900">Financing requests</h1>
          <p className="mt-1 text-sm text-neutral-500">
            {tex ? 'Every dealer request on the platform.' : 'Requests your dealership has opened. The buyer completes the application privately.'}
          </p>
        </div>
        <div className="flex items-end gap-2">
          {memberships.length > 1 && !tex && (
            <Field label="Dealership">
              <Select value={dealerId ?? ''} onChange={(e) => setSearch({ dealer: e.target.value || undefined })}>
                <option value="">Choose…</option>
                {memberships.map((m) => (
                  <DealerOption key={m.dealer_id} dealerId={m.dealer_id} />
                ))}
              </Select>
            </Field>
          )}
          {canCreate && dealerId && (
            <Button onClick={() => void navigate({ to: '/cases/new', search: { dealer: dealerId } })}>
              <Plus className="mr-1 h-4 w-4" aria-hidden /> New request
            </Button>
          )}
        </div>
      </div>

      {activeDealer?.status === 'suspended' && (
        <Alert tone="info">Your dealership is suspended: requests are read-only until Tex reinstates it.</Alert>
      )}
      {activeDealer?.status === 'pending_review' && (
        <Alert tone="info">Your dealership is still under review; new requests open once it is activated.</Alert>
      )}

      <SearchForm key={search.q ?? ''} search={search} setSearch={setSearch} />

      {needsDealer ? (
        <Alert tone="info">Choose which dealership to view.</Alert>
      ) : (
        <>
          {cases.isError && <Alert tone="error">{errorMessage(cases.error)}</Alert>}
          <div className="overflow-hidden rounded-xl border border-[var(--border-subtle)] bg-white shadow-sm">
            <table className="w-full text-sm">
              <thead className="border-b border-neutral-100 bg-neutral-50/60 text-left font-display text-[11px] font-bold uppercase tracking-wider text-neutral-500">
                <tr>
                  <th className="px-4 py-2">Reference</th>
                  <th className="px-4 py-2">Buyer</th>
                  {tex && <th className="px-4 py-2">Dealer</th>}
                  <th className="px-4 py-2">Equipment</th>
                  <th className="px-4 py-2 text-right">Requested</th>
                  <th className="px-4 py-2">Stage</th>
                  <th className="px-4 py-2">Opened</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.id} className="border-t border-neutral-100 transition-colors hover:bg-neutral-50/60">
                    <td className="px-4 py-2">
                      <Link to="/cases/$caseId" params={{ caseId: c.id }} className="font-medium text-tex-blue hover:underline">
                        <Mono>{c.reference}</Mono>
                      </Link>
                    </td>
                    <td className="px-4 py-2">
                      <div className="font-medium">{c.buyer_business_name}</div>
                      <div className="text-xs text-neutral-500">{c.buyer_contact_name}</div>
                    </td>
                    {tex && <td className="px-4 py-2 text-neutral-700">{c.dealer_legal_name ?? '—'}</td>}
                    <td className="px-4 py-2 text-neutral-700">{c.equipment_summary}</td>
                    <td className="px-4 py-2 text-right">
                      <Mono>{formatAmount(c.requested_principal)}</Mono>
                    </td>
                    <td className="px-4 py-2">
                      <StageBadge stage={c.stage} />
                    </td>
                    <td className="px-4 py-2 text-neutral-500">{formatDate(c.created_at)}</td>
                  </tr>
                ))}
                {cases.isSuccess && rows.length === 0 && (
                  <tr>
                    <td colSpan={tex ? 7 : 6} className="px-4 py-8 text-center text-neutral-500">
                      {search.q || search.stage ? 'No requests match these filters.' : 'No requests yet.'}
                    </td>
                  </tr>
                )}
                {cases.isPending && (
                  <tr>
                    <td colSpan={tex ? 7 : 6} className="px-4 py-8 text-center text-neutral-500">
                      Loading…
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {cases.hasNextPage && (
            <div className="flex justify-center">
              <Button variant="secondary" onClick={() => void cases.fetchNextPage()} disabled={cases.isFetchingNextPage}>
                {cases.isFetchingNextPage ? 'Loading…' : 'Load more'}
              </Button>
            </div>
          )}
        </>
      )}
    </section>
  )
}
